'use client';

import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { Application } from '../utils/nav.config';

import Badge from './Badge';
import { PlatformStats } from './types';
import { formatCompactNumber } from './utils/formatNumber';

interface PlatformStatsFooterProps {
	isUserLoggedIn: boolean | undefined;
	application?: Application;
	data?: PlatformStats;
	ssoLink: string | undefined;
}

export default function PlatformStatsFooter({ isUserLoggedIn, data, application = 'pickleball', ssoLink }: PlatformStatsFooterProps) {
	const [baseURL, setBaseURL] = useState('');
	const [isMobile, setIsMobile] = useState(false);
	const [windowWidth, setWindowWidth] = useState(0);

	useEffect(() => {
		setBaseURL(window.location.href);
	}, []);

	useEffect(() => {
		setIsMobile(windowWidth < 640);
	}, [windowWidth]);

	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			setWindowWidth(width);
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const isTourneyPage = application === 'tournament';
	const isTeamLeaguesPage = application === 'team-leagues';
	const isLeaguesPage = application === 'leagues';
	const isClubsPage = application === 'clubs';
	const isWPRRankingsPage = application === 'wpr-rankings';

	const isLocalHost = process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME?.startsWith('localhost');

	const bgClasses = clsx(
		'flex flex-col lg:flex-row gap-8 md:gap-12 py-4 lg:py-9 px-4 md:px-6 md:py-6 rounded-xl w-full max-w-screen-xl lg:justify-center',
		{
			'!bg-[#EF6820]': isTeamLeaguesPage,
			'bg-experiment': !isTourneyPage && !isTeamLeaguesPage && !isLeaguesPage && !isClubsPage && !isWPRRankingsPage,
			'!bg-[#008EED]': isTourneyPage,
			'!bg-[#F63D68]': isLeaguesPage,
			'!bg-[#DC1619]': isClubsPage,
			'!bg-[#4CA30D]': isWPRRankingsPage
		}
	);

	const textClasses = clsx('', {
		'!text-[#EF6820]': isTeamLeaguesPage,
		'text-experiment': !isTourneyPage && !isTeamLeaguesPage && !isLeaguesPage && !isClubsPage && !isWPRRankingsPage,
		'!text-[#008EED]': isTourneyPage,
		'!text-[#F63D68]': isLeaguesPage,
		'!text-[#DC1619]': isClubsPage,
		'!text-[#4CA30D]': isWPRRankingsPage
	});

	const buttonClasses = clsx('text-sm md:!text-2xl !bg-white', {
		'!text-[#EF6820]': isTeamLeaguesPage,
		'!text-experiment': !isTourneyPage && !isTeamLeaguesPage && !isLeaguesPage && !isClubsPage && !isWPRRankingsPage,
		'!text-[#008EED]': isTourneyPage,
		'!text-[#F63D68]': isLeaguesPage,
		'!text-[#DC1619]': isClubsPage,
		'!text-[#4CA30D]': isWPRRankingsPage
	});

	const badgeData = [
		{ heading: formatCompactNumber(data?.matchesPlayedCount), subHeading: 'Matches played' },
		// { heading: formatCompactNumber(data?.registeredUsersCount), subHeading: 'Registered users' },
		{ heading: formatCompactNumber(data?.tournamentsHostedCount), subHeading: 'Tournaments hosted' },
		{ heading: formatCompactNumber(data?.activeTournamentRecordsCount), subHeading: 'Active tournaments' },
		{ heading: formatCompactNumber(data?.activeOrganizationRecordsCount), subHeading: 'Active clubs' },
		{ heading: formatCompactNumber(data?.activeLeaguesCount), subHeading: 'Active leagues' }
	];

	return (
		<div className="w-full flex justify-center px-4 md:px-8 md:pt-8 pt-4">
			<div className={bgClasses}>
				{!isUserLoggedIn && (
					<div className="flex-1 flex-col md:text-left text-center">
						<div className="flex flex-col md:text-left text-center gap-4">
							<h3 className={clsx('text-2xl font-semibold md:text-3xl text-white')}>Join the fastest growing pickleball community</h3>
							<p className="text-sm text-white pb-8 md:text-base">
								Discover the scale of our growing community of players, tournaments, leagues, and clubs all in one place!{' '}
							</p>
						</div>
						<a
							href={
								isLocalHost
									? `/login?continue=${encodeURIComponent(baseURL)}`
									: `${ssoLink}/v3/signup?continue=${encodeURIComponent(baseURL)}`
							}
						>
							<Button
								variant="primary"
								size={isMobile ? 'lg' : '2xl'}
								suffixIcon={<FontAwesomeIcon icon={faArrowRight} className={textClasses} />}
								className={buttonClasses}
							>
								Join Pickleball.com
							</Button>
						</a>
					</div>
				)}

				<div className="grid grid-cols-2 md:grid-cols-3 justify-between gap-4 md:gap-6 lg:gap-4">
					{badgeData.map(({ heading, subHeading }) => (
						<Badge key={`${heading}-${subHeading}`} heading={heading} subHeading={subHeading} textColorClass={textClasses} />
					))}
				</div>
			</div>
		</div>
	);
}
